/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
import {Component, DestroyRef, DoCheck, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {CommonModule} from '@angular/common';
import {FormsModule, NgForm} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {MapService} from '../../services/map.service';
import {LookupService} from '../../../common/service/lookup.service';
import {EnlargementGraphicToolService} from './services/enlargement-graphic-tool.service';
import {EnlargementService} from './services/enlargement.service';
import {EnlargementSelectionService} from './services/enlargement-selection.service';
import {MapEventsService} from '../../services/map-events.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {DataFrameService} from '../../../common/service/data-frame-service';
import {PrintAreaService} from '../../../common/service/print-area.service';
import {MapModeService} from '../../services/map-mode.service';
import {DisplayPreferencesService} from '../../../common/service/display-preferences.service';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {MapToolsService} from '../../services/map-tools.service';
import {SpatialSnapshotService} from '../../snapshot/spatial-snapshot.service';
import {NgEventBus} from 'ng-event-bus';
import {EsriModuleService} from '../../esri-nlr/services/esri-module.service';
import {isDefined, isUndefined} from '../../../common/service/data-util';
import {NlrHasPermissionsPipe} from '../../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../../common/pipe/nlr-permissions-disabled-pipe';
import {WidgetService} from '../../services/widget.service';
import {NlrPatternDirective} from '../../../common/directive/nlr-pattern.directive';
import {MaxLengthInputLimitDirective} from '../../../common/directive/max-length-input-limit.directive';
import {MapEventsSelectionService} from '../../services/map-events-selection.service';
import {HelpLinkComponent} from '../../../help/help-link.component';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';
import {switchMap} from 'rxjs/operators';
import {EMPTY, first, map} from 'rxjs';
import {PermissionsService} from '../../../common/service/permissions.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-enlargement',
    imports: [
        CommonModule,
        FormsModule,
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe,
        NlrPatternDirective,
        MaxLengthInputLimitDirective,
        CdkDrag,
        CdkDragHandle,
        HelpLinkComponent,
        NgSelectModule
    ],
    providers: [CommonModule],
    templateUrl: './enlargement.component.html',
    styleUrl: './enlargement.component.scss'
})
export class EnlargementComponent implements OnInit, OnDestroy, DoCheck {
    @ViewChild('widget')
    widgetElement: HTMLElement;

    submitted: boolean;

    private refreshAnnotationInterval: number;

    private enlargementEditToolbar: any;

    widgetVisibleAtLastCheck: boolean;

    editViewDesignPermissions = ['EDIT_DESIGN', 'VIEW_DESIGN'];

    constructor(
        private mapService: MapService,
        private lookupService: LookupService,
        private enlargementGraphicToolService: EnlargementGraphicToolService,
        private enlargementService: EnlargementService,
        private enlargementSelectionService: EnlargementSelectionService,
        private mapEventsService: MapEventsService,
        private planModelService: PlanModelService,
        private dataFrameService: DataFrameService,
        private printAreaService: PrintAreaService,
        private mapModeService: MapModeService,
        private displayPreferencesService: DisplayPreferencesService,
        private spatialUtilService: SpatialUtilService,
        private mapToolsService: MapToolsService,
        private spatialSnapshotService: SpatialSnapshotService,
        private eventBus: NgEventBus,
        private esriModuleService: EsriModuleService,
        private widgetService: WidgetService,
        private mapEventsSelectionService: MapEventsSelectionService,
        private permissionsService: PermissionsService,
        private destroyRef: DestroyRef
    ) {}

    ngOnDestroy(): void {
        clearInterval(this.refreshAnnotationInterval);
    }

    @ViewChild('enlargementForm') form!: NgForm;
    ngOnInit(): void {
        console.debug(this.title + ' : initiated');

        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find(l => l.name === this.widgetId);
        this.setupInitialData();
        this.mapToolsService.setActivateFnForTool('ENLARGEMENT_WINDOW', () => this.setupInitialData());

        this.eventBus
            .on('enableFeaturesSelection')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.editPrintArea = !this.editPrintArea;
                setTimeout(() => {
                    this.editPrintArea = !this.editPrintArea;
                }, 100);
            });

        this.eventBus
            .on('closeEnlargementWidget')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(this.title + ' : closeEnlargementWidget by watch');
                this.closeWidget();
            });

        this.eventBus
            .on('clearSelectedFeatures')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(this.title + ' : Reset enlargement/clipping area selection by watch');
                if (this.enableClear) {
                    this.clickTool('clear-clipping');
                }
            });

        this.eventBus
            .on('enlargement-draw-end')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                const {flag, graphic, mapMode} = meta.data as any;
                this.enlargementAreaCreated(flag, graphic, mapMode);
            });

        this.eventBus
            .on('enlargement-graphic-move-stop')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                const {graphic, geometry} = meta.data as any;
                this.selectLineFeatures(graphic);
                this.updateClippingGeometry(geometry);
            });

        this.eventBus
            .on('enlargement-vertex-move-stop')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                const {graphic, geometry} = meta.data as any;
                this.selectLineFeatures(graphic);
                this.updateClippingGeometry(geometry);
            });

        this.eventBus
            .on('active-enlargement-window-click')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                this.enlargementEditToolbar = this.mapEventsService.enlargementEditToolbar;
                const {graphic, options} = meta.data as any;
                if (this.isCreateMode() || this.editPrintArea) {
                    this.enlargementEditToolbar.activate(this.esriModuleService.getEsri().toolbars.Edit.MOVE, graphic, options);
                } else {
                    this.enlargementEditToolbar.activate(
                        this.esriModuleService.getEsri().toolbars.Edit.MOVE |
                            this.esriModuleService.getEsri().toolbars.Edit.EDIT_VERTICES |
                            this.esriModuleService.getEsri().toolbars.Edit.SCALE |
                            this.esriModuleService.getEsri().toolbars.Edit.ROTATE,
                        graphic,
                        options
                    );
                }
            });

        this.eventBus
            .on('setup-map-events-click')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                let doSelect = true;

                const {evt, currentMap, enablePolygonPolylineSelection, scale} = meta.data as any;
                if (this.editPrintArea === true || this.editClipping === true) {
                    doSelect = false;
                }
                if (doSelect) {
                    this.mapToolsService.suspend('MAP_SELECTION_DRAG');
                    this.mapEventsSelectionService.selectOnClick(
                        evt,
                        currentMap,
                        enablePolygonPolylineSelection,
                        this.planModelService.getSpatialReference(),
                        scale
                    );
                    this.mapToolsService.restore('MAP_SELECTION_DRAG', {redraw: false});
                }
            });

        this.eventBus
            .on('update-enlargement-selection')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                const {newGraphic, oldGraphic, graphicBeforeMove} = meta.data as any;
                if (oldGraphic) {
                    this.reselectItems(oldGraphic, newGraphic);
                } else {
                    this.reselectItems(graphicBeforeMove, newGraphic);
                }
            });
    }

    ngDoCheck() {
        if (this.widgetElement && !this.widgetVisibleAtLastCheck) {
            this.widgetService.setupWidget(this.widgetId);
        }
        this.widgetVisibleAtLastCheck = !!this.widgetElement;
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    title = 'EnlargementComponent';
    widgetId = 'enlargement';
    widgetConfig = {
        header: undefined,
        collapseTooltip: undefined,
        expandTooltip: undefined,
        closeTooltip: undefined,
        subHeader: undefined
    };
    open = true; // Used to open and close accordion on UI

    attributes = [];
    type = 'enlargement';
    enlargementGraphic = undefined;

    // visibility flags
    enableCreateEnlargement = true;
    enableCreateClipping = false;
    enableClearClipping = false;
    editClipping = false;
    createClipping = false;
    enableClear = false;
    editPrintArea = false;
    createDisabled = false;

    priorAttributes = [];
    foundEnlargementFeature = undefined;
    clippingHoleGraphicGeometry = undefined;
    fullExtentGeometry = undefined;
    foundClippingFeatures = [];
    enlargementNames = []; // includes 'Main Plan'
    isValidName = true;
    updatedClippingGraphic = undefined;
    modifiedClippingArea = false;
    dupeEvent = false;
    modifiedEnlargement = false;
    clippingUnmaskedGeometry = undefined;
    showInformation = false;
    showErrors = true;
    errors = [];

    create() {
        console.debug(`${this.title} - create`);

        this.form.control.markAllAsTouched();
        this.submitted = true;
        if (this.isPrintAreaCreateInvalid()) {
            return;
        }

        this.validate();

        if (this.form.invalid) {
            console.debug(`${this.title} - create - form invalid`);
            return;
        }

        if (!this.isValidName) {
            return;
        }

        const attributes = {
            fullpage: undefined
        };

        this.attributes.forEach(attribute => {
            attributes[attribute.prop] = attribute.value;
        });

        if (this.enlargementGraphic) {
            if (this.isCreateMode()) {
                this.mapToolsService
                    .activate('ENLARGEMENT_CREATE', () => {
                        this.createDisabled = true;
                        // DCX-8404: get the next enlargement page and item order to use when creating new enlargement
                        const pageOrderItem = this.enlargementService.getNextEnlargementPageItemOrder(
                            attributes.fullpage.description === 'Full Page'
                        );

                        attributes['pageorder'] = pageOrderItem[0];
                        attributes['itemorder'] = pageOrderItem[1];
                        this.enlargementService.create(attributes, this.enlargementGraphic).subscribe(() => {
                            this.closeWidget();
                        });
                    })
                    .pipe(first())
                    .subscribe();
            } else {
                if (this.mapToolsService.isActive('ENLARGEMENT_PRINT_AREA_EDIT')) {
                    this.foundEnlargementFeature.geometry = this.spatialUtilService.copyGeometry(this.enlargementGraphic.geometry);
                    this.modifiedEnlargement = true;
                    this.clearMapGraphics();
                }

                this.enlargementService
                    .save(
                        attributes,
                        this.enlargementGraphic,
                        this.foundEnlargementFeature,
                        this.clippingHoleGraphicGeometry,
                        this.clippingUnmaskedGeometry,
                        this.printAreaService.getPaperSizeLookup(),
                        this.foundClippingFeatures,
                        this.modifiedEnlargement
                    )
                    .pipe(first())
                    .subscribe(() => {
                        this.setupInitialData();
                        this.closeWidget(false);
                    });

                if (this.mapToolsService.isActive('ENLARGEMENT_PRINT_AREA_EDIT')) {
                    this.displayPreferencesService.displayPreferences.showEnlargementFeatures = true;
                    this.displayPreferencesService.displayPreferences.showPrintArea = false;
                    this.eventBus.cast('setMapMode', true);
                    this.mapToolsService.deactivate('ENLARGEMENT_PRINT_AREA_EDIT');
                }
            }
        } else {
            if (this.isEditMode() && this.foundEnlargementFeature && this.foundEnlargementFeature.attributes) {
                this.enlargementService
                    .save(
                        attributes,
                        this.enlargementGraphic,
                        this.foundEnlargementFeature,
                        this.clippingHoleGraphicGeometry,
                        this.clippingUnmaskedGeometry,
                        this.printAreaService.getPaperSizeLookup(),
                        this.foundClippingFeatures,
                        this.modifiedEnlargement
                    )
                    .pipe(first())
                    .subscribe(() => {
                        this.mapToolsService.deactivate('ENLARGEMENT_CLIPPING_AREA');
                        this.setupInitialData();
                        this.closeWidget(false);
                    });
            }
        }
    }

    existsName(name) {
        this.isValidName =
            (this.foundEnlargementFeature && this.foundEnlargementFeature.attributes.name.replace(/[\s]/g, '') === name.replace(/[\s]/g, '')) ||
            this.enlargementNames.indexOf(name.replace(/[\s]/g, '').toLowerCase()) === -1;
        return !this.isValidName;
    }

    /*
     * Visibility methods
     */

    canEditPrintArea() {
        return this.isEditMode() && !this.editPrintArea;
    }

    canCreateClipping() {
        return (
            this.isEditMode() && isUndefined(this.clippingHoleGraphicGeometry) && !this.mapEventsService.drawToolbarActivated && !this.createClipping
        );
    }

    canEditClipping() {
        return this.isEditMode() && this.foundClippingFeatures.length > 0 && isDefined(this.clippingHoleGraphicGeometry) && !this.editClipping;
    }

    canClearClipping() {
        return !this.editClipping && (this.enableClearClipping || this.clippingHoleGraphicGeometry);
    }

    createOrSaveDisabled() {
        return this.mapToolsService.isSuspended('ENLARGEMENT_WINDOW');
    }

    deleteDisabled() {
        return (
            this.isCreateMode() ||
            this.editClipping ||
            this.editPrintArea ||
            !this.isValidName ||
            this.mapToolsService.isSuspended('ENLARGEMENT_WINDOW')
        );
    }

    editPrintAreaDisabled() {
        return (
            this.isCreateMode() ||
            this.mapEventsService.drawToolbarActivated ||
            this.createClipping ||
            this.editPrintArea ||
            this.editClipping ||
            this.mapToolsService.isSuspended('ENLARGEMENT_WINDOW')
        );
    }

    applyPrintAreaChangesDisabled() {
        return this.isCreateMode() || this.mapEventsService.drawToolbarActivated || this.createClipping || this.editClipping || !this.isValidName;
    }

    isPrintAreaDisabled() {
        return (
            this.mapToolsService.isSuspended('ENLARGEMENT_WINDOW') ||
            !this.enableCreateEnlargement ||
            !this.attributes.some(a => typeof a === 'object' && a['prop'] === 'scale' && a.value)
        );
    }

    createClippingDisabled() {
        return (
            this.isCreateMode() ||
            !this.enableCreateClipping ||
            this.editPrintArea ||
            this.mapEventsService.drawToolbarActivated ||
            this.mapToolsService.isSuspended('ENLARGEMENT_WINDOW')
        );
    }

    editClippingDisabled() {
        return this.isCreateMode() || this.editPrintArea || this.editClipping || this.mapToolsService.isSuspended('ENLARGEMENT_WINDOW');
    }

    clearClippingDisabled() {
        return (
            this.isCreateMode() ||
            !this.enableClearClipping ||
            !(this.clippingHoleGraphicGeometry || this.enableClearClipping) ||
            this.editPrintArea ||
            this.mapToolsService.isSuspended('ENLARGEMENT_WINDOW')
        );
    }

    split(text) {
        return text.split(',');
    }

    selectLineFeatures(graphic) {
        const lines = this.enlargementSelectionService.selectLineFeatures(graphic);
        this.spatialUtilService.highlightFeatures('line', lines);
    }
    reselectItems(oldGraphic, newGraphic) {
        this.enlargementGraphic = newGraphic;

        let newAnnotations = [];
        let newAngles = [];

        this.enlargementSelectionService
            .selectFeaturesForEnlargement(newGraphic)
            .pipe(
                switchMap(({annotations, angles}) => {
                    if (!this.enlargementGraphic) {
                        return EMPTY;
                    }

                    newAnnotations = annotations;
                    newAngles = angles;

                    this.spatialUtilService.highlightFeatures('annotation', newAnnotations);
                    this.spatialUtilService.highlightFeatures('angle', newAngles);

                    return this.enlargementSelectionService.selectFeaturesForEnlargement(oldGraphic);
                }),
                map(({annotations: oldAnnotations, angles: oldAngles}) => {
                    const deltaAnnotations = oldAnnotations.filter(old => !newAnnotations.some(n => n.attributes.id === old.attributes.id));

                    const deltaAngles = oldAngles.filter(old => !newAngles.some(n => n.attributes.id === old.attributes.id));

                    return {deltaAnnotations, deltaAngles};
                })
            )
            .subscribe(({deltaAnnotations, deltaAngles}) => {
                this.spatialUtilService.unhighlightFeatures('annotation', deltaAnnotations);
                this.spatialUtilService.unhighlightFeatures('angle', deltaAngles);
            });
    }
    scaleChanged(attribute, option) {
        console.debug(this.title + ' : scaleChanged');

        this.attributes.find(attr => attr.prop === attribute.prop).value = option;

        if (this.enableClear || this.editPrintArea) {
            this.enlargementAreaCreated(true, undefined, this.mapModeService.mapMode);
        }
    }

    getFullExtent(enlargementFeature) {
        const allFeatures = this.mapService.getAllFeatures();
        allFeatures.push(enlargementFeature);

        const e = this.mapService.getCalculatedExtentFromFeatures(allFeatures).expand(1.5);

        const polygon = new this.esriModuleService.Polygon(e.spatialReference);
        polygon.addRing([
            [e.xmin, e.ymin],
            [e.xmin, e.ymax],
            [e.xmax, e.ymax],
            [e.xmax, e.ymin],
            [e.xmin, e.ymin]
        ]);

        return this.spatialUtilService.project(polygon);
    }

    clearMapGraphics() {
        if (this.spatialUtilService.map && this.spatialUtilService.map.graphics) {
            this.spatialUtilService.map.graphics.clear();
        }
    }

    enlargementAreaCreated(flag, graphic, mapMode) {
        if (this.isCreateMode() || this.editPrintArea) {
            this.clearMapGraphics();

            let refScale = undefined;
            let fourPerPage = false;
            this.attributes.forEach(attribute => {
                if (typeof attribute === 'object') {
                    if (attribute['prop'] === 'scale' && attribute.value) {
                        refScale = attribute.value.referenceScale;
                    }
                    if (attribute['prop'] === 'fullpage' && attribute.value) {
                        fourPerPage = attribute.value.id !== 1;
                    }
                }
            });

            if (refScale) {
                // define an extent to identify the enlargement centroid, either from an existing enlargement graphic or the map bounds
                const enlargementExtent =
                    this.enlargementGraphic && this.enlargementGraphic.geometry
                        ? this.enlargementGraphic.geometry.getExtent()
                        : graphic && graphic.geometry
                          ? graphic.geometry.getExtent()
                          : this.mapService.map.extent;

                const spatialSettings = this.planModelService.getModel().spatialSettings;
                const coordSystemId = spatialSettings.projectedCoordSystem.id;
                const paperSizeId = this.printAreaService.getPaperSizeLookupId();
                const scale = parseInt(refScale.split(':')[1] || 1);
                const qtdPerPage = fourPerPage ? 4 : 1;
                const extent = {
                    xmin: enlargementExtent.xmin,
                    xmax: enlargementExtent.xmax,
                    ymin: enlargementExtent.ymin,
                    ymax: enlargementExtent.ymax
                };

                const dataFrameExtent = this.dataFrameService.getDataFrame(coordSystemId, paperSizeId, scale, qtdPerPage, extent);

                const polygon = new this.esriModuleService.Polygon(
                    new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference())
                );
                polygon.addRing([
                    [dataFrameExtent.xmin, dataFrameExtent.ymin],
                    [dataFrameExtent.xmin, dataFrameExtent.ymax],
                    [dataFrameExtent.xmax, dataFrameExtent.ymax],
                    [dataFrameExtent.xmax, dataFrameExtent.ymin],
                    [dataFrameExtent.xmin, dataFrameExtent.ymin]
                ]);

                const symbol = new this.esriModuleService.SimpleFillSymbol(
                    this.esriModuleService.SimpleFillSymbol.STYLE_SOLID,
                    new this.esriModuleService.SimpleLineSymbol(
                        this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                        this.esriModuleService.Color([0, 112, 255, 0.25]),
                        6
                    ),
                    new this.esriModuleService.Color([0, 112, 255, 0.25]),
                    1
                );

                const newGraphic = new this.esriModuleService.Graphic(polygon, symbol, {type: 'enlargementGraphic'});

                this.spatialUtilService.addMapGraphic(null, newGraphic);
                this.enlargementGraphic = newGraphic;

                this.enlargementSelectionService.selectFeaturesForEnlargement(newGraphic).subscribe(({annotations, angles}) => {
                    this.spatialUtilService.highlightFeatures('annotation', annotations);
                    this.spatialUtilService.highlightFeatures('angle', angles);
                });
            }
        } else if (this.foundEnlargementFeature && mapMode.type !== 'mainPlan') {
            this.enlargementGraphicToolService.differencePolygon(graphic.geometry, this.getFullExtent(this.foundEnlargementFeature), result => {
                if (result) {
                    this.clippingUnmaskedGeometry = this.spatialUtilService.copyGeometry(graphic.geometry);
                    graphic.geometry = this.spatialUtilService.copyGeometry(result.geometry);
                    this.clearMapGraphics();
                    graphic.attributes = {type: 'clippingGraphic'};
                    this.spatialUtilService.addMapGraphic(null, graphic);
                    this.clippingHoleGraphicGeometry = graphic.geometry;
                }
            });
        }

        if (!this.editPrintArea) {
            this.enableCreateEnlargement = !flag;
            this.enableCreateClipping = !flag;
            this.enableClear = flag;
        }
    }

    updateClippingGeometry(polygonGeometry) {
        this.clippingHoleGraphicGeometry = polygonGeometry;
        this.clippingUnmaskedGeometry = this.spatialUtilService.copyGeometry(this.clippingHoleGraphicGeometry);
    }

    finishClippingDrawingManually() {
        console.log('DSA -> finishClippingDrawingManually');

        if (this.mapEventsService.enlargementDrawToolbar._graphic) {
            console.log('Triggering manual clipping finish drawing');
            this.mapEventsService.enlargementDrawToolbar.finishDrawing();
        }
    }

    clickTool(tool) {
        console.debug(this.title + ' : ' + tool + ' : clicked');

        // init editPrintArea flag
        this.editPrintArea = false;

        switch (tool.trim()) {
            case 'set-print-area':
                this.mapToolsService
                    .activate(
                        'ENLARGEMENT_PRINT_AREA_CREATE',
                        () => this.enlargementAreaCreated(true, undefined, this.mapModeService.mapMode),
                        () => {
                            setTimeout(() => {
                                this.enableCreateEnlargement = true;
                                this.clearMapGraphics();
                                this.spatialUtilService.unhighlightFeatures('angle');
                                this.spatialUtilService.unhighlightFeatures('annotation');
                                this.enlargementGraphic = undefined;
                            }, 500);
                        }
                    )
                    .subscribe();
                break;
            case 'clear-clipping':
                this.enlargementGraphicToolService.deactivateTool(this.mapEventsService.enlargementEditToolbar);
                this.enlargementGraphicToolService.deactivateTool(this.mapEventsService.enlargementDrawToolbar);
                this.clearMapGraphics();

                if (this.foundClippingFeatures.length > 0) {
                    this.modifiedClippingArea = true;
                    this.updatedClippingGraphic = undefined;
                }

                this.enableCreateClipping = this.foundClippingFeatures.length === 0;

                if (this.enlargementGraphic) {
                    this.enableCreateEnlargement = true;
                }

                this.clippingHoleGraphicGeometry = undefined;
                this.clippingUnmaskedGeometry = undefined;
                this.enlargementGraphic = undefined;

                this.editClipping = false;
                this.enableClearClipping = false;

                this.createClipping = false;

                this.mapEventsService.drawToolbarActivated = false;

                this.mapToolsService.deactivate('ENLARGEMENT_CLIPPING_AREA');

                break;
            case 'create-clipping':
                this.mapToolsService
                    .activate(
                        'ENLARGEMENT_CLIPPING_AREA',
                        () => {
                            const method = this.mapService.getCursorMode();

                            this.mapService.map.setMapCursor(this.mapService['CROSS_' + method + '_CURSOR']);
                            this.enlargementGraphicToolService.activateTool(this.mapEventsService.enlargementDrawToolbar, 'POLYGON');
                            this.mapEventsService.drawToolbarActivated = true;

                            this.enableClearClipping = true;
                            this.enableCreateClipping = false;
                            this.createClipping = true;
                        },
                        () => {
                            this.editClipping = false;
                            this.enableClearClipping = false;
                            this.enableCreateClipping = this.foundClippingFeatures.length === 0;
                            this.createClipping = false;
                            this.mapEventsService.drawToolbarActivated = false;
                            this.enlargementGraphicToolService.deactivateTool(this.mapEventsService.enlargementEditToolbar);
                            this.enlargementGraphicToolService.deactivateTool(this.mapEventsService.enlargementDrawToolbar);
                            this.clearMapGraphics();
                            this.clickTool('clear-clipping');
                        }
                    )
                    .subscribe();
                break;
            case 'edit-clipping':
                this.mapToolsService
                    .activate(
                        'ENLARGEMENT_CLIPPING_AREA',
                        () => {
                            // create clipping graphic
                            this.displayPreferencesService.displayPreferences.showEnlargementFeatures = true;
                            this.displayPreferencesService.displayPreferences.showClippingFeatures = false;
                            this.displayPreferencesService.displayPreferences.showPrintArea = false;

                            this.eventBus.cast('setMapMode', true);

                            const symbol = new this.esriModuleService.SimpleFillSymbol(
                                this.esriModuleService.SimpleFillSymbol.STYLE_SOLID,
                                new this.esriModuleService.SimpleLineSymbol(
                                    this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                                    new this.esriModuleService.Color([0, 112, 255]),
                                    4
                                ),
                                new this.esriModuleService.Color([0, 112, 255, 0.17]),
                                1
                            );

                            this.updatedClippingGraphic = new this.esriModuleService.Graphic(
                                this.spatialUtilService.copyGeometry(this.clippingHoleGraphicGeometry),
                                symbol,
                                {
                                    type: 'clippingGraphic'
                                }
                            );

                            this.clearMapGraphics();
                            this.spatialUtilService.addMapGraphic(null, this.updatedClippingGraphic);

                            const polygonGeometry = new this.esriModuleService.Polygon();
                            polygonGeometry.setSpatialReference(this.clippingHoleGraphicGeometry.spatialReference);
                            polygonGeometry.addRing(this.clippingHoleGraphicGeometry.rings[1]);
                            const graphic = new this.esriModuleService.Graphic(this.spatialUtilService.copyGeometry(polygonGeometry));
                            this.selectLineFeatures(graphic);

                            this.editClipping = true;
                            this.enableClearClipping = true;
                        },
                        () => {
                            setTimeout(() => {
                                this.editClipping = false;
                                this.enableClearClipping = false;
                                this.mapEventsService.drawToolbarActivated = false;
                                this.enlargementGraphicToolService.deactivateTool(this.mapEventsService.enlargementEditToolbar);
                                this.enlargementGraphicToolService.deactivateTool(this.mapEventsService.enlargementDrawToolbar);
                                this.clearMapGraphics();
                            }, 500);
                            this.clickTool('clear-clipping');
                        }
                    )
                    .subscribe();
                break;
            case 'apply-clipping':
                {
                    this.editClipping = false;
                    this.enlargementGraphic = this.updatedClippingGraphic;
                    this.clippingHoleGraphicGeometry = this.updatedClippingGraphic.geometry;

                    const polygonGeometry = new this.esriModuleService.Polygon();
                    polygonGeometry.setSpatialReference(this.clippingHoleGraphicGeometry.spatialReference);
                    polygonGeometry.addRing(this.clippingHoleGraphicGeometry.rings[1]);

                    this.clippingUnmaskedGeometry = polygonGeometry;
                    this.modifiedClippingArea = true;

                    this.create();

                    this.enableClearClipping = true;

                    this.mapToolsService.deactivate('ENLARGEMENT_CLIPPING_AREA');
                    this.setupInitialData();
                }
                break;
            case 'update-clipping-cancel':
                {
                    this.editClipping = false;
                    this.clearMapGraphics();
                    this.updatedClippingGraphic = undefined;

                    this.displayPreferencesService.displayPreferences.showEnlargementFeatures = true;
                    this.displayPreferencesService.displayPreferences.showClippingFeatures = true;
                    this.displayPreferencesService.displayPreferences.showPrintArea = false;

                    this.eventBus.cast('setMapMode', true);

                    this.enableClearClipping = true;

                    this.mapToolsService.deactivate('ENLARGEMENT_CLIPPING_AREA');
                }
                break;
            case 'edit-print-area':
                {
                    this.mapToolsService
                        .activate(
                            'ENLARGEMENT_PRINT_AREA_EDIT',
                            () => {
                                this.editPrintArea = true;
                                this.priorAttributes = this.attributes
                                    .filter(attribute => {
                                        return ['fullpage', 'scale'].indexOf(attribute.prop) !== -1;
                                    })
                                    .map(attribute => {
                                        return window.structuredClone(attribute);
                                    });

                                this.displayPreferencesService.displayPreferences.showEnlargementFeatures = true;
                                this.displayPreferencesService.displayPreferences.showClippingFeatures = false;
                                this.displayPreferencesService.displayPreferences.showPrintArea = false;

                                this.eventBus.cast('setMapMode', true);

                                this.enlargementAreaCreated(true, this.foundEnlargementFeature, this.mapModeService.mapMode);
                            },
                            () => {
                                setTimeout(() => {
                                    this.editPrintArea = false;
                                    this.clearMapGraphics();
                                    this.spatialUtilService.unhighlightFeatures('angle');
                                    this.spatialUtilService.unhighlightFeatures('annotation');
                                }, 500);
                            }
                        )
                        .subscribe();
                }
                break;
            case 'apply-create-enlargement':
            case 'apply-create-clipping':
            case 'update-print-area':
                this.create();
                break;
            case 'update-print-area-cancel':
                {
                    this.clearMapGraphics();

                    this.priorAttributes.forEach(priorAttribute => {
                        this.attributes.forEach(attribute => {
                            if (attribute.prop === priorAttribute.prop) {
                                attribute.value = priorAttribute.value;
                            }
                        });
                    });

                    this.displayPreferencesService.displayPreferences.showEnlargementFeatures = true;
                    this.displayPreferencesService.displayPreferences.showPrintArea = false;

                    this.eventBus.cast('setMapMode', true);

                    this.enlargementGraphic = undefined;

                    this.mapToolsService.deactivate('ENLARGEMENT_PRINT_AREA_EDIT');
                }
                break;
        }
    }

    validate() {
        console.debug(this.title + ' : validate');

        this.errors = [];

        if (this.existsName(this.form.value.enlargementname)) {
            this.errors.push('The entered enlargement label has already been used and is not unique. Please enter another label.');
        }

        this.form.onSubmit(undefined);

        this.form.control.markAllAsTouched();
        this.submitted = true;

        if (this.errors.length > 0) {
            this.showErrors = true;
        }
    }

    setupEnlargementAttributes(callback?) {
        console.debug(this.title + ' : setupEnlargementAttributes');

        this.attributes = [];

        this.lookupService.getLookups('enlargementAttributes').forEach(attribute => {
            if (attribute.attributeType === 'DOMAIN' && attribute.lookupService !== null) {
                const lookupValues = this.lookupService.getLookups(attribute.lookupService);

                attribute.values = lookupValues
                    .filter(item => {
                        if (Object.prototype.hasOwnProperty.call(item, 'display')) {
                            return item.display === true;
                        } else if (Object.prototype.hasOwnProperty.call(item, 'enlargement')) {
                            return item.enlargement === true;
                        } else {
                            return true;
                        }
                    })
                    .sort((lookup1, lookup2) => (lookup1.uiOptionOrder > lookup2.uiOptionOrder ? 1 : -1));

                if (attribute.lookupDefaultId !== null || (this.isEditMode() && this.foundEnlargementFeature)) {
                    attribute.values.forEach((lookup, index) => {
                        if (this.isEditMode() && this.foundEnlargementFeature) {
                            if (new String(lookup.description).toString() === this.foundEnlargementFeature.attributes[attribute.prop]) {
                                attribute.value = lookup;
                            }
                            if (
                                lookup['referenceScale'] &&
                                new String(lookup.referenceScale).toString() === ' 1:' + this.foundEnlargementFeature.attributes[attribute.prop]
                            ) {
                                attribute.value = lookup;
                            }
                        } else {
                            if (new String(lookup.id).toString() === attribute.lookupDefaultId) {
                                attribute.value = lookup;
                            }
                        }
                    });
                }
            } else if (attribute.attributeType.indexOf('RADIO') !== -1) {
                attribute.value = attribute.lookupDefaultId;

                if (attribute.labels) {
                    let items = attribute.labels.split(',');

                    if (items.length !== 2) {
                        items = [];
                        items[0] = 'Yes';
                        items[1] = 'No';
                    }
                    attribute.values = [
                        {value: 'YES', description: items[0]},
                        {value: 'NO', description: items[1]}
                    ];
                } else {
                    attribute.values = [
                        {value: 'YES', description: 'Yes'},
                        {value: 'NO', description: 'No'}
                    ];
                }
            } else if (attribute.attributeType.indexOf('BOOLEAN') !== -1) {
                attribute.value = attribute.lookupDefaultId;
            } else if (attribute.attributeType.indexOf('STRING') !== -1) {
                attribute.attributeTypeLength = this.getAttributeTypeLength(attribute.attributeType);

                if (this.isEditMode() && this.foundEnlargementFeature) {
                    if (
                        isDefined(this.foundEnlargementFeature.attributes[attribute.prop]) &&
                        this.foundEnlargementFeature.attributes[attribute.prop] !== null
                    ) {
                        attribute.value = this.foundEnlargementFeature.attributes[attribute.prop];
                    }
                }
            } else if (attribute.attributeType.indexOf('DECIMAL') !== -1) {
                const decimalLength = this.getAttributeTypeLength(attribute.attributeType);
                attribute.pattern = new RegExp(`^-?\\d{1,15}(\\.\\d{1,${decimalLength}})?$`);
            } else if (attribute.attributeType.indexOf('INTEGER') !== -1) {
                attribute.value = Number(attribute.lookupDefaultId);
                attribute.attributeTypeLength = this.getAttributeTypeLength(attribute.attributeType);
            }

            this.attributes.push(attribute);
        });

        this.attributes.push({prop: 'guid', value: this.planModelService.getGuid()});

        if (callback) {
            callback();
        }
    }

    getAttributeTypeLength(attributeType) {
        console.debug(this.title + ' : getAttributeTypeLength');

        const lengthStartIndex = attributeType.indexOf('(') + 1;
        const lengthEndIndex = attributeType.indexOf(')');
        return attributeType.substring(lengthStartIndex, lengthEndIndex);
    }

    setupInitialData() {
        console.debug(this.title + ' : setupInitialData');

        this.setupTools();
        if (this.isEditMode()) {
            this.widgetConfig.header = 'Edit Enlargement';
            this.widgetConfig.subHeader = 'Edit Enlargement';

            this.displayPreferencesService.displayPreferences.showPrintArea = false;

            this.eventBus.cast('setMapMode', false);

            const enlargements = this.spatialUtilService.getEnlargements();
            this.foundEnlargementFeature = enlargements.find(enlargement => enlargement.attributes.name === this.mapModeService.mapMode.label);

            this.foundClippingFeatures = [];
            this.clippingHoleGraphicGeometry = undefined;
            this.clippingUnmaskedGeometry = undefined;

            if (!this.foundEnlargementFeature) {
                this.enableCreateClipping = true;
                this.setupEnlargementAttributes();
            } else {
                const clippingFeatures = this.spatialUtilService
                    .getClippings()
                    .filter(f => f.attributes.enlargementid === this.foundEnlargementFeature.attributes.id);

                if (clippingFeatures && clippingFeatures.length > 0) {
                    clippingFeatures.forEach(item => {
                        if (item.attributes.type === 1) {
                            this.foundClippingFeatures.push(item);
                            this.clippingHoleGraphicGeometry = this.spatialUtilService.copyGeometry(item.geometry);

                            this.enableClearClipping = true;
                            this.enableCreateClipping = false;
                        }

                        if (item.attributes.type === 2) {
                            //for backward compatibility
                            this.foundClippingFeatures.push(item);
                            this.clippingUnmaskedGeometry = this.spatialUtilService.copyGeometry(item.geometry);
                        }
                    });
                } else {
                    this.enableCreateClipping = true;
                }
                this.setupEnlargementAttributes();
            }
        } else {
            this.widgetConfig.header = 'Create Enlargement';
            this.widgetConfig.subHeader = 'Create Enlargement';
            this.setupEnlargementAttributes();
        }
    }

    isCreateMode() {
        return this.mapModeService.mapMode.type === 'mainPlan';
    }

    isEditMode() {
        return !this.isCreateMode();
    }

    setupTools() {
        if (this.isEditMode()) {
            this.enableCreateEnlargement = false;
            this.enableClear = false;
            this.isValidName = true;
            this.createClipping = false;
        } else {
            this.enableCreateEnlargement = true;
            this.enableClear = false;
            this.isValidName = false;
            this.createClipping = false;
        }

        this.enableCreateClipping = this.foundClippingFeatures.length === 0;

        this.enlargementNames = [];
        this.mapModeService.mapModeList.forEach(item => {
            this.enlargementNames.push(item.label.replace(/[\s]/g, '').toLowerCase());
        });
    }

    clear() {
        console.debug(this.title + ' controller clear');
        this.clickTool('clear-clipping');
        this.enableClear = false;
        this.setupInitialData();
        this.form.form.markAsPristine();
        this.errors = [];
        this.mapToolsService.deactivate('ENLARGEMENT');
    }

    delete() {
        this.spatialSnapshotService.createSnapshot({showAlert: true}).subscribe(() => {
            this.mapToolsService.deactivate('ENLARGEMENT');

            this.clearMapGraphics();

            const existingAnnotationCount = this.spatialUtilService.getAnnotations().length;

            this.refreshAnnotationInterval = setInterval(
                () => {
                    if (this.spatialUtilService.getAnnotations().length || existingAnnotationCount === 0) {
                        clearInterval(this.refreshAnnotationInterval);

                        this.enlargementService.delete(this.attributes, this.mapModeService.mapMode.enlargementFeature, this.foundClippingFeatures);
                    }
                },
                600,
                5
            );
        });
    }

    closeWidget(flag?) {
        console.debug(this.title + ' closeWidget');

        this.enlargementGraphicToolService.deactivateTool(this.mapEventsService.enlargementDrawToolbar);
        if (isUndefined(flag) || (isDefined(flag) && flag === true)) {
            this.mapToolsService.deactivate('ENLARGEMENT_WINDOW');
        }

        this.clearMapGraphics();

        this.mapEventsService.currentMap.graphics.graphics.forEach(graphic => {
            if (graphic.attributes && (graphic.attributes['type'] === 'enlargementGraphic' || graphic.attributes['type'] === 'clippingGraphic')) {
                this.spatialUtilService.removeMapGraphic(graphic);
            }
        });

        this.modifiedEnlargement = false;
        this.enlargementGraphic = undefined;
        this.mapEventsService.drawToolbarActivated = false;
    }

    resizeEnlargementBody() {
        console.debug(this.title + ' controller: resizeEnlargementBody');
        const widget = document.getElementById(this.widgetId);
        const widgetBody = document.querySelector('.annotation-tool-body');

        if (widget && widgetBody && (widgetBody as HTMLElement).offsetTop) {
            const widgetBodyHeight = widget.offsetHeight - (widgetBody as HTMLElement).offsetTop + 50;
            (widgetBody as HTMLElement).style.height = `${widgetBodyHeight}`;
        }
    }

    isPrintAreaCreateInvalid() {
        return this.isCreateMode() && !this.enlargementGraphic && !this.isPrintAreaDisabled();
    }

    printAreaCreateButtonHasError() {
        return (this.isCreateMode() && !this.enlargementGraphic && this.form?.submitted && this.submitted && this.isValidName) ?? false;
    }

    getHelpUrl() {
        return HELP_LINK_CONTENTS_BASE_URL + `enlargement-${this.isEditMode() ? 'edit' : 'create'}-help.html`;
    }

    getHelpPageTitle() {
        return `Enlargement Tool | ${this.isEditMode() ? 'Edit Enlargement' : 'Create an Enlargement'}`;
    }

    getTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('ENLARGEMENT_WINDOW', 'Enlargement Tool');
    }

    onClick() {
        this.setupInitialData();
        this.widgetService.onClick('Enlargement');
    }

    isWidgetDisabled() {
        return (
            this.widgetService.isWidgetDisabled('Enlargement', this.mapModeService.mapMode) ||
            this.planModelService.readonly ||
            this.permissionsService.isDisabledByPermissions(this.editViewDesignPermissions)
        );
    }

    isWidgetActive() {
        return this.widgetService.isWidgetActive('Enlargement');
    }

    showOrHideWidgetContent() {
        this.widgetService.showOrHideWidgetContent(this.widgetId, this.open);
        this.open = !this.open;
    }

    trackByIndex(index: number): number {
        return index;
    }

    hasRequiredError(fieldName) {
        if (!this.form) {
            return false;
        }
        return this.hasError(fieldName) && this.form.controls[fieldName].errors['required'];
    }

    checkEnlargementname() {
        if (!this.form) {
            return false;
        }
        return this.form.controls['enlargementname']?.dirty ?? false;
    }

    hasError(controlName: string): boolean {
        if (!this.form) {
            return false;
        }
        return (this.form.controls[controlName]?.invalid && (this.form.controls[controlName]?.dirty || this.form.submitted)) ?? false;
    }

    checkNameAttributeValue(attribute) {
        return attribute.prop === 'name' && attribute.value?.length > attribute.attributeTypeLength;
    }

    getValueForAttribute(attribute: any): string {
        return attribute?.value?.description || attribute?.value?.referenceScale || 'Select';
    }

    createOrSave(): void {
        let tool: string | null = null;

        if (this.isCreateMode()) {
            tool = this.createClipping ? 'create-clipping' : 'apply-create-enlargement';
        } else if (this.createClipping) {
            tool = 'apply-create-clipping';
        } else if (this.editPrintArea) {
            tool = 'update-print-area';
        } else if (this.editClipping) {
            tool = 'apply-clipping';
        }

        if (tool) {
            this.clickTool(tool);
        } else {
            this.create();
        }
    }
}
